// frontend/src/components/AgentForm.jsx

import React, { useState, useEffect } from 'react';
import { EMAIL_PREFERENCES, US_STATES } from '../constants';

const AgentForm = ({ agent, onSubmit, submitLabel = 'Submit' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    selectedStates: [],
    emailPreference: EMAIL_PREFERENCES.IMMEDIATE,
    isAdmin: false,
    notes: '',
    ...agent // Spread existing agent data if provided
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  
  useEffect(() => {
    if (agent) {
      setFormData(prevData => ({
        ...prevData,
        ...agent,
        password: '' // Clear password when editing
      }));
    }
  }, [agent]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name.startsWith('state-')) {
      const stateCode = name.replace('state-', '');
      const updatedStates = checked 
        ? [...formData.selectedStates, stateCode]
        : formData.selectedStates.filter(s => s !== stateCode);
      
      setFormData(prev => ({
        ...prev,
        selectedStates: updatedStates
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const generatePassword = () => {
    const adjectives = [
      'Happy', 'Brave', 'Clever', 'Jolly', 'Lucky', 'Magic', 'Noble', 'Quiet', 'Swift', 'Witty',
      'Bright', 'Calm', 'Eager', 'Fair', 'Gentle', 'Kind', 'Proud', 'Wise', 'Zesty', 'Honest'
    ];
    
    const nouns = [
      'Apple', 'Bear', 'Cloud', 'Daisy', 'Eagle', 'Fish', 'Garden', 'Horse', 'Island', 'Jacket',
      'Kitten', 'Lion', 'Mountain', 'Notebook', 'Ocean', 'Penguin', 'River', 'Sunflower', 'Tiger', 'Unicorn'
    ];
    
    const getRandomElement = (array) => array[Math.floor(Math.random() * array.length)];
    const randomNum = Math.floor(Math.random() * 999) + 100; // 3-digit number
    
    const password = `${getRandomElement(adjectives)}${getRandomElement(nouns)}${randomNum}!`;
    
    setFormData(prev => ({
      ...prev,
      password
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    }
    if (!agent && !formData.password) {
      newErrors.password = 'Password is required';
    }

    if (Object.keys(newErrors).length === 0) {
      onSubmit(formData);
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-background-secondary p-6 rounded-lg shadow">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-text-primary">
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-button-primary-bg focus:border-button-primary-bg bg-background-tertiary text-text-primary"
          />
          {errors.name && <p className="mt-1 text-sm text-status-error-text">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-text-primary">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-button-primary-bg focus:border-button-primary-bg bg-background-tertiary text-text-primary"
          />
          {errors.email && <p className="mt-1 text-sm text-status-error-text">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-text-primary">
            Password {!agent && <span className="text-status-error-text">*</span>}
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-button-primary-bg focus:border-button-primary-bg bg-background-tertiary text-text-primary"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-0 pr-3 flex items-center mt-1"
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-text-secondary">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-text-secondary">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-sm text-status-error-text">{errors.password}</p>}
          {!agent && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => {
                  generatePassword();
                }}
                className="text-sm text-button-primary-bg hover:text-button-primary-hover"
              >
                Generate secure password
              </button>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-text-primary">
            Your Ideal Prospect Criteria
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Describe your ideal prospect criteria (e.g., investment amount range, retirement timeline, specific annuity types, etc.)"
            className="mt-1 block w-full px-3 py-2 border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-button-primary-bg focus:border-button-primary-bg bg-background-tertiary text-text-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Select States
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 bg-background-tertiary rounded-lg border border-border-primary">
            {Object.entries(US_STATES).map(([stateCode, stateName]) => (
              <div key={stateCode} className="flex items-center">
                <input
                  type="checkbox"
                  id={`state-${stateCode}`}
                  name={`state-${stateCode}`}
                  checked={formData.selectedStates.includes(stateCode)}
                  onChange={handleChange}
                  className="h-4 w-4 text-button-primary-bg focus:ring-button-primary-bg border-border-primary rounded"
                />
                <label htmlFor={`state-${stateCode}`} className="ml-2 text-sm text-text-primary">
                  {stateName}
                </label>
              </div>
            ))}
          </div>
          {formData.selectedStates.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-text-tertiary">Selected states ({formData.selectedStates.length}):</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {formData.selectedStates.map(stateCode => (
                  <span
                    key={stateCode}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-status-info-bg text-status-info-text"
                  >
                    {US_STATES[stateCode] || stateCode}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          selectedStates: prev.selectedStates.filter(s => s !== stateCode)
                        }));
                      }}
                      className="ml-1 hover:text-status-info-border"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="emailPreference" className="block text-sm font-medium text-text-primary">
            Email Notification Preference
          </label>
          <select
            id="emailPreference"
            name="emailPreference"
            value={formData.emailPreference}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-button-primary-bg focus:border-button-primary-bg bg-background-tertiary text-text-primary"
          >
            {Object.entries(EMAIL_PREFERENCES).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="isAdmin"
            name="isAdmin"
            checked={formData.isAdmin}
            onChange={handleChange}
            className="h-4 w-4 rounded border-border-primary text-button-primary-bg focus:ring-button-primary-bg"
          />
          <label htmlFor="isAdmin" className="ml-2 block text-sm text-text-primary">
            Admin Access
          </label>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className="inline-flex justify-center rounded-md border border-transparent bg-button-primary-bg py-2 px-4 text-sm font-medium text-button-primary-text shadow-sm hover:bg-button-primary-hover focus:outline-none focus:ring-2 focus:ring-button-primary-bg focus:ring-offset-2"
        >
          {agent ? 'Update Agent' : 'Create Agent'}
        </button>
      </div>
    </form>
  );
};

export default AgentForm;